import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTodos,
  Actualizar,
  Eliminar,
  Insertar,
} from "../../redux/razonesSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import Required from "../../Components/Required";
import useSubmit from "../../Hooks/useSubmit";
import { OrderTable } from "../../Components/OrderTable";
import { getAdminReasonPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaRazones";
function Razones(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminReasonPage, setAdminReasonPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const dispatch = useDispatch();
  let busqueda = useRef();

  const { submitAction } = useSubmit();

  const { razones } = useSelector((state) => state.razones);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminReasonPage?.reason_table ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "razonId", sortable: true },
      { 
        label: table[1]?.label || "Nombre", 
        accessor: "razonNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.razonNombre] || item.razonNombre}</td>
        ),
      },
      { label: table[2]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminReasonPage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setTituloModal(adminReasonPage?.subtitleO || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    reset({ razonId: 0, razonNombre: "" });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarRazones = () => {
    dispatch(ObtenerTodos());
  };

  const cargarRazonesPorCaracter = () => {
    dispatch(ObtenerTodos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(Eliminar, itemSelected.razonId, () => {
      closeRemoveModalEdit();
      cargarRazones();
    });
  };

  const onSubmit = async (data) => {
    submitAction(data.razonId > 0 ? Actualizar : Insertar, data, () => {
      closeModalEdit();
      cargarRazones();
    });
  };

  useEffect(() => {
    cargarRazones();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminReasonPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminReasonPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminReasonPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasRazones = razones && Array.isArray(razones) && razones.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasRazones) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre"
      const translationPromises = razones.map(async (item) => {
        if (item.razonNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.razonNombre, locale);
          nombreTranslations[item.razonNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [razones, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 400, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminReasonPage?.subtitleTR || "Agregar");
              setValue("razonNombre", "");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminReasonPage?.title || "Razones para elegir CENFOTEC "}</h2>
        </div>

        <div className="header-admin-bar-items ml-auto">
          <label hidden>búsqueda</label>
          <input
            type="search"
            className="form-control"
            placeholder={adminReasonPage?.search_input || "Búsqueda"}
            onInput={cargarRazonesPorCaracter}
            ref={busqueda}
          />
        </div>
      </div>

      <div className="table-container">
        {razones.length > 0 ? (
          <OrderTable
            {...{ columns }}
            data={razones}
            clase="table-adm"
            actions={[
              {
                icon: "",
                clase: "text-primary",
                style: { display: "contents" },
                event: (item) => showModalEdit(item),
              },
              {
                icon: "",
                clase: "delete",
                style: { display: "contents" },
                event: (item) => showRemoveModalEdit(item),
              },
            ]}
          />
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminReasonPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminReasonPage?.reason || "Razón"}</label>
            <Required />
            <input
              className="form-control"
              name="razonNombre"
              {...register("razonNombre", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.razonNombre && (
              <p className="error">{errors.razonNombre.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminReasonPage?.subtitleT || "Eliminar plantillla"}
        message={adminReasonPage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected.razonNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminReasonPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminReasonPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default Razones;
